// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  QUIZSEO.JSX - VERSION: 2026.01.12.1400                                     ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// Dynamic SEO meta tags for quiz pages
// Sets unique <title>, <meta description>, and <h1> for each quiz
// 2026.01.12.1400 - Added seo_description support for longer meta descriptions
// ============================================================================
import React from 'react';
import { Helmet } from 'react-helmet';

// Generate a description from quiz name if none provided
const generateDescription = (quizName, questionCount) => {
  const name = quizName || 'Quiz';
  const count = questionCount ? `${questionCount} questions` : 'multiple questions';
  
  // Create a natural description based on the quiz name
  const descriptions = {
    // Add specific descriptions for key quizzes
    'christmas-quiz': `Test your Christmas knowledge with our festive holiday quiz! ${count} covering traditions, songs, movies and more. Play free on TRAKtag.`,
    'baking': `How well do you know baking? Test your knowledge of cakes, breads, techniques and ingredients. ${count} to challenge your culinary skills!`,
    'all-about-trains': `All aboard! Test your railway knowledge with questions about trains, locomotives, and rail history. Perfect for train enthusiasts!`,
    'are-you-safe-online': `Test your internet safety knowledge! Learn about online security, privacy, and staying safe on the web. Essential quiz for all ages.`,
    'chocolate-bars-and-brands': `Can you identify famous chocolate bars and brands? Sweet trivia for chocolate lovers! ${count} of delicious fun.`,
    'chocolate-basics': `Test your chocolate knowledge! From cocoa beans to famous brands, see how much you know about the world's favourite treat.`,
    'british-cities': `How well do you know British cities? Test your knowledge of UK geography, landmarks and city facts.`,
    'cockney-rhyming-slang': `Know your apples and pears? Test your Cockney rhyming slang knowledge with this fun London-themed quiz!`,
    'famous-songs-about-people': `Can you match famous songs to the people they're about? Music trivia for all generations!`,
    '10-downing-street': `Test your knowledge of 10 Downing Street, British Prime Ministers and UK political history.`,
    'animal-sounds': `Can you match animals to their sounds? Fun quiz for all ages - test your wildlife knowledge!`,
    'trainspotting': `Test your British railway trainspotting knowledge! Questions about locomotives, stations, famous trains and rail history. Perfect for rail enthusiasts!`,
  };
  
  // Check for specific description, otherwise generate generic one
  const slug = quizName?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  if (descriptions[slug]) {
    return descriptions[slug];
  }
  
  // Generic description
  return `Play the ${name} quiz on TRAKtag! Test your knowledge with ${count}. Challenge yourself, earn TRAKs, and compete with friends. Free to play!`;
};

// Format quiz name for title
const formatTitle = (quizName) => {
  if (!quizName) return 'Quiz | TRAKtag';
  return `${quizName} Quiz | TRAKtag - Free Trivia Game`;
};

// Main SEO Component
const QuizSEO = ({ quiz, questionCount }) => {
  if (!quiz) return null;
  
  const title = formatTitle(quiz.name || quiz.game_name);
  // Priority: seo_description > description > generated description
  const description = quiz.seo_description || quiz.description || generateDescription(quiz.name || quiz.game_name, questionCount);
  const canonicalUrl = `https://traktag.com/quiz/${quiz.slug}`;
  const imageUrl = quiz.icon_url || 'https://traktag.com/TRAKtag_Logo_Full.png';
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content="TRAKtag" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      
      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="keywords" content={`${quiz.name}, quiz, trivia, TRAKtag, free quiz game, test knowledge`} />
    </Helmet>
  );
};

// Visible SEO content (H1 and intro text for Google)
export const QuizSEOContent = ({ quiz, questionCount }) => {
  if (!quiz) return null;
  
  // Priority: seo_description > description > generated description
  const description = quiz.seo_description || quiz.description || generateDescription(quiz.name || quiz.game_name, questionCount);
  
  return (
    <div className="sr-only" aria-hidden="false">
      {/* Hidden but crawlable H1 */}
      <h1>{quiz.name || quiz.game_name} Quiz - TRAKtag</h1>
      <p>{description}</p>
    </div>
  );
};

export default QuizSEO;
